import { Component, OnInit, AfterViewInit, DestroyRef } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd, RouterModule } from '@angular/router';
import { CountryInterface } from '../../../shared/interfaces/country-interface';
import { TranslateService } from '@ngx-translate/core';
import { LocalizeRouterModule, LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { ResolutionService } from '../../../core/services/resolution.service';
import { NavigationService } from '../../../core/services/navigation.service';
import { BookingParametersInterface } from '../../../shared/entities/bookingParameters';
import { ShopService } from '../../../shared/services/shop.service';
import { ResponseService } from '../../../core/services/response.service';
import {
  PlatformassetPipe,
  PlatformInformationService
} from '../../../../../projects/platform-manager/src/public_api';
import { Meta, Title } from '@angular/platform-browser';
import { ZoomInService } from '../../../shared/services/zoom-in.service';
import { Town } from '../../../shared/entities/town';
import { FOLD_OUT_ANIMATION } from '../../../core/animations/FOLD_OUT_ANIMATION';
import { AlpitranslatePipe } from '../../../core/pipes/alpitranslate.pipe';
import { ShopSearchComponent } from '../../../shared/landing-page/search/shop-search/shop-search.component';
import { chevronDown, IconComponent, provideIcons } from '../../../shared/icons';
import { CrowdinTranslationService } from '../../../shared/services/crowdin-translation.service';
import { loaderMap } from './translations/loader-map';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-shop-zoom-in',
  templateUrl: './shop-zoom-in.component.html',
  styleUrls: ['./shop-zoom-in.component.scss'],
  animations: [FOLD_OUT_ANIMATION],
  imports: [
    AlpitranslatePipe,
    ShopSearchComponent,
    IconComponent,
    RouterModule,
    PlatformassetPipe,
    LocalizeRouterModule
  ],
  providers: [provideIcons({ chevronDown })]
})
export class ShopZoomInComponent implements OnInit, AfterViewInit {
  zoomInName: string;
  error = false;
  loading = true;
  country: string;
  province: string;
  countries: Array<CountryInterface> = [];
  provinces;
  towns;
  skihireentry = new Map<string, string>();
  showSearchForm: boolean;
  showCountry = true;
  showProvince = true;
  showTowns = true;
  isMobile = this.resolutionService.isSmallerThanLargeDevice();

  constructor(
    private title: Title,
    private route: ActivatedRoute,
    private router: Router,
    private localizeRouterService: LocalizeRouterService,
    private zoomInService: ZoomInService,
    public translateService: TranslateService,
    public resolutionService: ResolutionService,
    protected navigationService: NavigationService,
    private shopService: ShopService,
    private responseService: ResponseService,
    private platformService: PlatformInformationService,
    private crowdinService: CrowdinTranslationService,
    private destroyRef: DestroyRef,
    meta: Meta
  ) {
    meta.addTag({ name: 'robots', content: 'index, follow' });
    this.showSearchForm =
      ['ar', 'mc', 'df', 'du', 'bp'].indexOf(this.platformService.getCurrentPlatformShortcode()) !== -1;
  }

  get lang() {
    return this.translateService.currentLang;
  }

  private provincesForCountry() {
    if (this.countries.length && this.country) {
      const _country = this.countries.find(country => this.isCountrySelected(country));
      if (_country) {
        return _country.provinces.sort(this.sortCountriesByName());
      } else {
        // no country in list found. show 404
        this.responseService.show404Page();
      }
    }
    return [];
  }

  private sortTownsByName(towns: Town[]) {
    return towns.sort(function (a, b) {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    });
  }

  private townsForProvince() {
    if (this.provinces.length && this.province) {
      // province is selected, get towns for province or 404
      const prov = this.provinces.find(province => this.isProvinceSelected(province));
      if (prov) {
        return this.sortTownsByName(prov.towns);
      } else {
        // no province in list found. show 404
        this.responseService.show404Page();
      }
    }
    // no province selected yet, show all towns in country
    const towns = [];
    this.provinces.forEach(province => {
      province.towns.forEach(town => {
        towns.push(town);
      });
    });
    return this.sortTownsByName(towns);
  }

  ngOnInit() {
    this.crowdinService.watchLangChange({ loaderMap: loaderMap, destroyRef: this.destroyRef });
    this.resolutionService.sizeReferenceChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.isMobile = this.resolutionService.isSmallerThanLargeDevice();
    });
    this.updateRouterLink();
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      if (params.has('country')) {
        this.showCountry = false;
        this.country = params.get('country');
        this.province = null;
        this.provinces = this.provincesForCountry();
        this.towns = this.townsForProvince();
      }
      if (params.has('province')) {
        this.showProvince = false;
        this.province = params.get('country') + '/' + params.get('province');
        this.towns = this.townsForProvince();
      } else {
        this.province = null;
      }
      if (!this.country) {
        this.showCountry = true;
      }
    });
    this.loadZoomInData();
    this.translateService.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.updateRouterLink();
      this.updateTitle();
      this.countries = this.countries.sort(this.sortCountriesByName());
    });
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(routerEvent => {
      if (routerEvent instanceof NavigationEnd) {
        const country = this.getCountry();
        const province = this.getProvince();
        if (country && province) {
          const url = this.localizeRouterService.translateRoute(
            '/SKIHIREENTRY/' + this.getProvince().slugs[this.translateService.currentLang]
          );
          this.router.navigate([url]);
        } else if (country) {
          const url = this.localizeRouterService.translateRoute(
            '/SKIHIREENTRY/' + this.getCountry().slugs[this.translateService.currentLang]
          );
          this.router.navigate([url]);
        }
        this.updateTitle();
      }
    });
  }

  ngAfterViewInit() {
    this.updateTitle();
  }

  selectedCountry(country) {
    const url = this.localizeRouterService.translateRoute('/SKIHIREENTRY/' + country);
    this.router.navigate([url]);
  }

  selectedProvince(province) {
    const url = this.localizeRouterService.translateRoute('/SKIHIREENTRY/' + province);
    this.router.navigate([url]);
    this.updateTitle();
  }

  selectedTown(town) {
    const url = this.localizeRouterService.translateRoute('/SKIHIREENTRY/' + town);
    this.router.navigate([url]);
  }

  private sortCountriesByName() {
    return (a, b) => {
      if (a.names[this.translateService.currentLang] < b.names[this.translateService.currentLang]) {
        return -1;
      }
      if (a.names[this.translateService.currentLang] > b.names[this.translateService.currentLang]) {
        return 1;
      }
      return 0;
    };
  }

  private loadZoomInData() {
    this.zoomInService
      .getZoomInData()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.error = false;
          this.loading = false;
          this.countries = data.countries.sort(this.sortCountriesByName());
          this.provinces = this.provincesForCountry();
          this.towns = this.townsForProvince();
          this.updateTitle();
        },
        error: () => {
          this.countries = [];
          this.error = true;
        }
      });
  }

  getCountry(): CountryInterface | null {
    if (this.countries && this.country) {
      return this.countries.find(country => this.isCountrySelected(country));
    }
    return null;
  }

  getProvince() {
    const country = this.getCountry();
    if (country) {
      return country.provinces.find(province => this.isProvinceSelected(province));
    }
    return null;
  }

  get getSearchFormName() {
    if (this.getCountry()) {
      return this.getCountry().names[this.translateService.currentLang];
    }
    return '';
  }

  get provinceName() {
    if (this.getProvince()) {
      return this.getProvince().names[this.translateService.currentLang];
    }
    return '';
  }

  get countryName() {
    if (this.getCountry()) {
      return this.getCountry().names[this.translateService.currentLang];
    }
    return '';
  }

  isCountrySelected(country) {
    if (Object.prototype.hasOwnProperty.call(country, 'slugs')) {
      for (const key in country.slugs) {
        if (this.country === country.slugs[key]) {
          return true;
        }
      }
    }
    return false;
  }

  isProvinceSelected(province) {
    for (const key in province.slugs) {
      if (this.province === province.slugs[key]) {
        return true;
      }
    }
    return false;
  }

  updateRouterLink() {
    this.skihireentry.set(this.lang, this.localizeRouterService.translateRoute('/SKIHIREENTRY') as string);
  }

  tryReload() {
    this.loadZoomInData();
  }

  goTo(event: BookingParametersInterface) {
    this.shopService.updateBookingParameters(event);
    if (event.poi) {
      this.navigationService.navigateToPoi(event.poi);
    } else {
      this.navigationService.navigateToSearchPage(event.slug);
    }
  }

  /**
   * set page title
   */
  protected updateTitle() {
    let code: string;
    if (this.country && this.province) {
      code = 'titleWithCountryAndProvince';
    } else if (this.country) {
      code = 'titleWithCountry';
    } else {
      code = 'titleWithNothing';
    }
    this.translateService
      .get('ALPINSKIHIRE.zoomIn.' + code, {
        country: this.countryName,
        province: this.provinceName
      })
      .subscribe((res: string) => {
        this.title.setTitle(res);
      });
  }

  toggle(selectionbox: string) {
    switch (selectionbox) {
      case 'country':
        this.showCountry = !this.showCountry;
        break;
      case 'province':
        this.showProvince = !this.showProvince;
        break;
      case 'town':
        this.showTowns = !this.showTowns;
        break;
    }
  }

  getHalfPoint(selectionbox: string): number {
    switch (selectionbox) {
      case 'country':
        return Math.ceil(this.countries.length / 2);
        break;
      case 'province':
        return Math.ceil(this.provinces.length / 2);
        break;
      case 'town':
        return Math.ceil(this.towns.length / 2);
        break;
    }
  }

  getCountryCode(country: string) {
    switch (country) {
      case 'andorra':
        return 'ad';
      case 'austria':
        return 'at';
      case 'bulgaria':
        return 'bg';
      case 'czech-republic':
        return 'cz';
      case 'france':
        return 'fr';
      case 'germany':
        return 'de';
      case 'italy':
        return 'it';
      case 'poland':
        return 'pl';
      case 'slovakia':
        return 'sk';
      case 'spain':
        return 'es';
      case 'switzerland':
        return 'ch';
      default:
        return null;
    }
  }
}
